import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { 
  AboutUsSection,
  ServicesSection,
  AnimatedMirsalText,
  IntroSection,
  ProblemStatementSection,
  SolutionOverviewSection,
  HowItWorksSection,
  PricingSection,
  ContactSection,
} from '../components/Landing';

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const heroSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;

  const introSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const problemSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const solutionSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const howItWorksSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const aboutUsSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const valuesSectionRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const relationshipRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const respectRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const trustworthinessRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const valuePeopleRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const servicesSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const pricingSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const contactSectionRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;
  const footerRef = useRef<HTMLElement>(null) as React.RefObject<HTMLElement>;

  // Reset scroll position on page load/refresh
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const textContainer = heroSectionRef.current?.querySelector(
      '[data-hero-text]'
    ) as HTMLElement | null;
    if (textContainer) {
      textContainer.style.transform = 'scale(1)';
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    const timeout = setTimeout(scrollToTop, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  // Scale hero text on scroll — desktop only. Applying transform during touch
  // scroll on iOS fights the scrollport (short drag then snap back to top).
  useEffect(() => {
    const heroElement = heroSectionRef.current;
    if (!heroElement) return;

    const finePointer = window.matchMedia('(pointer: fine)');
    if (!finePointer.matches) return;

    let rafId: number | null = null;
    const textContainer = heroElement.querySelector(
      '[data-hero-text]'
    ) as HTMLElement | null;

    const getViewportHeight = () =>
      window.visualViewport?.height ?? window.innerHeight;
    let windowHeight = getViewportHeight();

    const updateTransform = () => {
      const scrollY = window.scrollY;
      const scrollProgress = Math.min(scrollY / windowHeight, 1);
      const scale = 1 - scrollProgress * 0.7;

      if (textContainer) {
        textContainer.style.transform = `scale(${scale})`;
      }

      rafId = null;
    };

    const handleScroll = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(updateTransform);
      }
    };

    const handleResize = () => {
      windowHeight = getViewportHeight();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Scroll-based reveal using Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const sectionId =
            element.id || element.dataset?.valueId || element.dataset?.sectionId || 'footer';
          setVisibleSections((prev) => new Set(prev).add(sectionId));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const refs = [
      introSectionRef,
      aboutUsSectionRef,
      valuesSectionRef,
      relationshipRef,
      respectRef,
      trustworthinessRef,
      valuePeopleRef,
      servicesSectionRef,
      pricingSectionRef,
      problemSectionRef,
      solutionSectionRef,
      howItWorksSectionRef,
      contactSectionRef,
      footerRef,
    ];

    refs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className="bg-black text-white w-full landing-page"
      style={{
        // Avoid overflow-x: clip — it breaks vertical scrolling on iOS Safari.
        // Horizontal spill is already handled on html/body.
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/logo.jpg" 
                alt="Mirsal Logo" 
                className="h-10 w-10 rounded-lg object-cover"
              />
            </div>

            {/* Right Side Navigation and Actions */}
            <div className="flex items-center space-x-4 sm:space-x-8">
              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <a href="#about-us" className="font-montserrat text-gray-300 hover:text-white transition-colors text-sm lg:text-base">
                  ABOUT US
                </a>
                <a href="#services" className="font-montserrat text-gray-300 hover:text-white transition-colors text-sm lg:text-base">
                  SERVICES
                </a>
                {/* <a href="#pricing" className="font-montserrat text-gray-300 hover:text-white transition-colors text-sm lg:text-base">
                  PRICING
                </a> */}
                <a href="#contact" className="font-montserrat text-gray-300 hover:text-white transition-colors text-sm lg:text-base">
                  CONTACT US
                </a>
                <Link to="/login">
                  <button className="font-montserrat text-gray-300 hover:text-white transition-colors text-sm lg:text-base">
                    SIGN IN
                  </button>
                </Link>
              </nav>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-white p-2 -mr-2"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-gray-800">
            <div className="px-4 py-6 space-y-4">
              <a
                href="#about-us"
                onClick={() => setIsMenuOpen(false)}
                className="font-montserrat block text-gray-300 hover:text-white transition-colors py-2"
              >
                ABOUT US
              </a>
              <a
                href="#services"
                onClick={() => setIsMenuOpen(false)}
                className="font-montserrat block text-gray-300 hover:text-white transition-colors py-2"
              >
                SERVICES
              </a>
              {/* <a
                href="#pricing"
                onClick={() => setIsMenuOpen(false)}
                className="font-montserrat block text-gray-300 hover:text-white transition-colors py-2"
              >
                PRICING
              </a> */}
              <a
                href="#contact"
                onClick={() => setIsMenuOpen(false)}
                className="font-montserrat block text-gray-300 hover:text-white transition-colors py-2"
              >
                CONTACT US
              </a>
              <div className="pt-4 border-t border-gray-800">
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <button className="font-montserrat block text-gray-300 hover:text-white transition-colors py-2">
                    SIGN IN
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="relative">
        {/* Hero — in-flow; text is pointer-events-none so swipes scroll the page */}
        <section
          ref={heroSectionRef}
          className="relative z-10 flex min-h-[100svh] h-[100svh] items-center justify-center overflow-hidden bg-black"
          style={{ touchAction: 'pan-y' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none" />

          <div
            className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none select-none"
            data-hero-text
            style={{
              transformOrigin: 'center center',
              WebkitUserSelect: 'none',
              userSelect: 'none',
            }}
            aria-hidden="true"
          >
            <AnimatedMirsalText />
          </div>
        </section>

        {/* Intro Section */}
        <IntroSection
          visibleSections={visibleSections}
          introSectionRef={introSectionRef}
        />

        {/* About Us Section */}
        <AboutUsSection
          visibleSections={visibleSections}
          refs={{
            aboutUsSectionRef,
            valuesSectionRef,
            relationshipRef,
            respectRef,
            trustworthinessRef,
            valuePeopleRef,
          }}
        />

        {/* Our Services Section */}
        <ServicesSection
          visibleSections={visibleSections}
          expandedService={expandedService}
          setExpandedService={setExpandedService}
          servicesSectionRef={servicesSectionRef}
        />

        {/* Pricing Section - Hidden for now */}
        {/* <PricingSection
          visibleSections={visibleSections}
          SectionRef={pricingSectionRef}
        />

        {/* Problem Statement Section */}
        <ProblemStatementSection
          visibleSections={visibleSections}
          problemSectionRef={problemSectionRef}
        />

        {/* Solution Overview Section */}
        <SolutionOverviewSection
          visibleSections={visibleSections}
          solutionSectionRef={solutionSectionRef}
        />

        {/* How It Works Section */}
        <HowItWorksSection
          visibleSections={visibleSections}
          howItWorksSectionRef={howItWorksSectionRef}
        />

        {/* Contact Section */}
        <ContactSection
          visibleSections={visibleSections}
          contactSectionRef={contactSectionRef}
        />
      </main>

      {/* Footer */}
      <footer 
        ref={footerRef}
        id="footer"
        className={`relative z-10 border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8 bg-black transition-all duration-1000 ${
          visibleSections.has('footer') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Section Divider */}
        <div
          className={`mb-8 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 ${
            visibleSections.has('footer')
              ? 'opacity-100 scale-x-100'
              : 'opacity-0 scale-x-0'
          }`}
        />

        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Mirsal Logo" 
                className="h-8 w-8 rounded-lg object-cover"
              />
            </div>
            <p className="text-gray-400">
              © {new Date().getFullYear()} Mirsal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
